import React, { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useTheme } from "../contexts/ThemeContext";
import { Activity, Plus, Trash2, Upload } from "lucide-react";
import InvoiceUpload from "../components/health/InvoiceUpload";
import HealthRecordModal from "../components/health/HealthRecordModal";

const HealthRecordsPage = ({ user, userRole }) => {
  const { themeClasses } = useTheme();
  const [healthRecords, setHealthRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);

  // Initialize Firebase Functions
  const functions = getFunctions(app);

  // Helper function to format currency using database currency information
  const formatCurrency = (amount, currency) => {
    if (!amount) return `${currency?.symbol || "$"}0.00`;

    // Use the currency detected and stored in the database
    const symbol = currency?.symbol || "$";
    const formattedAmount = amount.toFixed(2);

    return `${symbol}${formattedAmount}`;
  };

  useEffect(() => {
    if (user && (userRole === "pro" || userRole === "admin")) {
      fetchHealthRecords();
    }
  }, [user, userRole]);

  const fetchHealthRecords = async () => {
    try {
      setLoading(true);
      const listHealthRecordsFn = httpsCallable(functions, "listHealthRecords");
      const result = await listHealthRecordsFn({});

      if (result.data.success) {
        const records = result.data.healthRecords.map((data) => ({
          id: data.id,
          petId: data.petId,
          petName: data.petName,
          date: data.createdAt || new Date().toISOString(),
          type: data.recordType,
          notes: data.notes,
          documentUrl: data.documentUrl,
          keyFindings: data.keyFindings || [],
          medications: data.medications || [],
          procedures: data.procedures || [],
          costs: data.costs || [],
          dates: data.dates || [],
          veterinarian: data.veterinarian || "",
          clinic: data.clinic || "",
          tags: data.tags || [],
          extractionMethod: data.extractionMethod || "",
          // Enhanced financial data
          lineItems: data.lineItems || [],
          totalCost: data.totalCost || null,
          invoiceDate: data.invoiceDate || null,
          totalAmount: data.totalAmount || data.totalCost || 0,
          clinicName: data.clinicName || data.clinic || "",
          location: data.location || "",
          // Use detected currency from database
          currency: {
            symbol: data.currencySymbol || "$",
            code: data.currencyCode || "USD",
            source: data.currencySource || "default",
          },
          documentLanguage: data.documentLanguage || "unknown",
          languageConfidence: data.languageConfidence || 0,
        }));

        setHealthRecords(records);
        console.log(
          `Successfully loaded ${records.length} health records:`,
          records
        );

        // Debug line items structure and other fields
        records.forEach((record, index) => {
          console.log(`Health Record ${index + 1} overview:`, {
            petName: record.petName,
            invoiceDate: record.invoiceDate,
            scanDate: record.date,
            tags: record.tags,
            documentUrl: record.documentUrl,
            totalCost: record.totalCost,
            totalAmount: record.totalAmount,
          });

          if (record.lineItems && record.lineItems.length > 0) {
            console.log(
              `Health Record ${index + 1} line items:`,
              record.lineItems
            );
            record.lineItems.forEach((item, itemIndex) => {
              console.log(`  Item ${itemIndex + 1}:`, item);
              console.log(`    Available price fields:`, {
                totalPrice: item.totalPrice,
                unitPrice: item.unitPrice,
                price: item.price,
                cost: item.cost,
                amount: item.amount,
                total: item.total,
                allKeys: Object.keys(item),
              });
            });
          }
        });
      } else {
        console.error("Failed to load health records:", result.data.message);
        setHealthRecords([]);
      }
    } catch (error) {
      console.error("Error fetching health records:", error);
      setHealthRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHealthRecord = async (recordId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this health record? This action cannot be undone."
      )
    ) {
      try {
        const deleteHealthRecordFn = httpsCallable(
          functions,
          "deleteHealthRecord"
        );
        const result = await deleteHealthRecordFn({ recordId });

        if (result.data.success) {
          setHealthRecords((records) =>
            records.filter((record) => record.id !== recordId)
          );
        } else {
          console.error("Failed to delete health record:", result.data.message);
        }
      } catch (error) {
        console.error("Error deleting health record:", error);
      }
    }
  };

  if (userRole === "free") {
    return (
      <div className="text-center py-12">
        <Activity
          className={`w-16 h-16 ${themeClasses.text.tertiary} mx-auto mb-4`}
        />
        <h2 className={`text-2xl font-bold ${themeClasses.text.primary} mb-2`}>
          Health Records
        </h2>
        <p className={`${themeClasses.text.secondary} mb-6`}>
          Upgrade to Pro to track your pets' medical history and expenses
        </p>
        <button
          className={`px-6 py-3 ${themeClasses.bg.button} text-white rounded-lg transition-colors`}
        >
          Upgrade to Pro
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2
          className={`text-xl lg:text-2xl font-bold ${themeClasses.text.primary} flex items-center`}
        >
          <Activity
            className={`w-6 h-6 lg:w-8 lg:h-8 ${themeClasses.text.accent} mr-3`}
          />
          Health Records
        </h2>
        <button
          onClick={() => setShowUpload(true)}
          className={`${themeClasses.bg.button} text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 flex items-center`}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Health Record
        </button>
      </div>

      {/* Health Records List */}
      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6 animate-pulse`}
              >
                <div
                  className={`h-4 ${themeClasses.bg.secondary} rounded w-1/4 mb-2`}
                ></div>
                <div
                  className={`h-3 ${themeClasses.bg.secondary} rounded w-1/2 mb-4`}
                ></div>
                <div
                  className={`h-3 ${themeClasses.bg.secondary} rounded w-3/4`}
                ></div>
              </div>
            ))}
          </div>
        ) : healthRecords.length === 0 ? (
          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-8 text-center`}
          >
            <Activity
              className={`w-16 h-16 ${themeClasses.text.tertiary} mx-auto mb-4`}
            />
            <p className={`${themeClasses.text.secondary} mb-4`}>
              No health records found.
            </p>
            <button
              onClick={() => setShowUpload(true)}
              className={`${themeClasses.bg.button} text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 flex items-center mx-auto`}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add First Health Record
            </button>
          </div>
        ) : (
          healthRecords.map((record, index) => (
            <div
              key={index}
              className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6 hover:scale-[1.02] transition-transform`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3
                      className={`text-lg font-semibold ${themeClasses.text.primary}`}
                    >
                      {record.petName || "Unknown Pet"}
                    </h3>
                    {record.type && (
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${themeClasses.bg.secondary} ${themeClasses.text.accent} border ${themeClasses.border.primary}`}
                      >
                        {record.type}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 mb-2">
                    {record.invoiceDate && (
                      <p
                        className={`${themeClasses.text.accent} text-sm font-medium`}
                      >
                        📅 Invoice Date:{" "}
                        {(() => {
                          try {
                            // Handle different date formats
                            let date;
                            if (record.invoiceDate.toDate) {
                              // Firestore timestamp
                              date = record.invoiceDate.toDate();
                            } else if (typeof record.invoiceDate === "string") {
                              // String date
                              date = new Date(record.invoiceDate);
                            } else {
                              // Already a Date object
                              date = new Date(record.invoiceDate);
                            }

                            if (isNaN(date.getTime())) {
                              return record.invoiceDate; // Return original if can't parse
                            }
                            return date.toLocaleDateString();
                          } catch {
                            return record.invoiceDate; // Return original if error
                          }
                        })()}
                      </p>
                    )}
                    <p className={`${themeClasses.text.tertiary} text-xs`}>
                      📤 Scanned:{" "}
                      {(() => {
                        try {
                          let date;
                          if (record.date.toDate) {
                            date = record.date.toDate();
                          } else {
                            date = new Date(record.date);
                          }
                          return date.toLocaleDateString();
                        } catch {
                          return new Date(record.date).toLocaleDateString();
                        }
                      })()}
                    </p>
                  </div>

                  {/* Clinic and Veterinarian Info */}
                  <div className="space-y-1 mb-3">
                    {record.veterinarian && (
                      <p
                        className={`${themeClasses.text.secondary} text-sm flex items-center`}
                      >
                        <span className={`${themeClasses.text.success} mr-2`}>
                          👨‍⚕️
                        </span>
                        <strong>Dr. {record.veterinarian}</strong>
                      </p>
                    )}
                    {record.clinic && (
                      <p
                        className={`${themeClasses.text.secondary} text-sm flex items-center`}
                      >
                        <span className={`${themeClasses.text.accent} mr-2`}>
                          🏥
                        </span>
                        <strong>{record.clinic}</strong>
                      </p>
                    )}
                    {record.location && (
                      <p
                        className={`${themeClasses.text.tertiary} text-sm flex items-center`}
                      >
                        <span className={`${themeClasses.text.accent} mr-2`}>
                          📍
                        </span>
                        {record.location}
                      </p>
                    )}
                  </div>

                  {/* Tags */}
                  {record.tags && record.tags.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1">
                        {record.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className={`inline-flex items-center px-2 py-1 text-xs font-medium ${themeClasses.bg.tertiary} ${themeClasses.text.accent} border ${themeClasses.border.primary} rounded-full`}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cost Information - Always visible */}
                  <div
                    className={`${themeClasses.bg.tertiary} rounded-lg p-3 mb-3 border ${themeClasses.border.primary}`}
                  >
                    <div className="flex justify-between items-center">
                      <p
                        className={`text-sm ${themeClasses.text.success} font-mono`}
                      >
                        💰 Total Cost:{" "}
                        {formatCurrency(
                          record.totalAmount ||
                            record.totalCost ||
                            (record.lineItems
                              ? record.lineItems.reduce(
                                  (sum, item) =>
                                    sum +
                                    (item.totalPrice || item.unitPrice || 0),
                                  0
                                )
                              : 0),
                          record.currency
                        )}
                      </p>
                      {record.documentUrl && (
                        <a
                          href={record.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center space-x-1 text-xs ${themeClasses.text.accent} hover:${themeClasses.text.primary} ${themeClasses.bg.secondary} px-2 py-1 rounded border ${themeClasses.border.primary} hover:${themeClasses.bg.tertiary} transition-colors`}
                        >
                          <span>📄</span>
                          <span>View Original</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-2">
                  <div className="flex items-center space-x-2">
                    {record.documentUrl && (
                      <a
                        href={record.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-2 ${themeClasses.text.accent} hover:${themeClasses.text.primary} hover:${themeClasses.bg.secondary} rounded-lg transition-colors`}
                        title="View original document"
                      >
                        <Upload className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => handleDeleteHealthRecord(record.id)}
                      className={`p-2 ${themeClasses.text.error} hover:${themeClasses.text.error} hover:${themeClasses.bg.secondary} rounded-lg transition-colors`}
                      title="Delete health record"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {record.extractionMethod && (
                    <span
                      className={`text-xs ${themeClasses.text.tertiary} ${themeClasses.bg.secondary} px-2 py-1 rounded`}
                    >
                      {record.extractionMethod}
                    </span>
                  )}
                </div>
              </div>

              {/* Key Findings */}
              {record.keyFindings && record.keyFindings.length > 0 && (
                <div className="mb-4">
                  <h4
                    className={`text-sm font-semibold ${themeClasses.text.accent} mb-2 flex items-center`}
                  >
                    <span className="mr-2">🔍</span>
                    Key Findings ({record.keyFindings.length}):
                  </h4>
                  <div
                    className={`${themeClasses.bg.secondary} rounded-lg p-3 border ${themeClasses.border.primary}`}
                  >
                    <ul
                      className={`${themeClasses.text.secondary} text-sm space-y-1`}
                    >
                      {record.keyFindings.slice(0, 3).map((finding, idx) => (
                        <li key={idx} className="flex items-start">
                          <span
                            className={`${themeClasses.text.accent} mr-2 mt-1`}
                          >
                            •
                          </span>
                          <span className="flex-1">{finding}</span>
                        </li>
                      ))}
                      {record.keyFindings.length > 3 && (
                        <li
                          className={`${themeClasses.text.accent} text-xs mt-2`}
                        >
                          +{record.keyFindings.length - 3} more findings...
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Medications */}
              {record.medications && record.medications.length > 0 && (
                <div className="mb-4">
                  <h4
                    className={`text-sm font-semibold ${themeClasses.text.accent} mb-2 flex items-center`}
                  >
                    <span className="mr-2">💊</span>
                    Medications ({record.medications.length}):
                  </h4>
                  <div
                    className={`${themeClasses.bg.secondary} rounded-lg p-3 border ${themeClasses.border.primary}`}
                  >
                    <ul
                      className={`${themeClasses.text.secondary} text-sm space-y-2`}
                    >
                      {record.medications.slice(0, 5).map((medication, idx) => (
                        <li key={idx} className="flex items-start">
                          <span
                            className={`${themeClasses.text.accent} mr-2 mt-1`}
                          >
                            💊
                          </span>
                          <div className="flex-1">
                            {typeof medication === "object" ? (
                              <div
                                className={`${themeClasses.bg.tertiary} rounded p-2`}
                              >
                                <span
                                  className={`font-medium ${themeClasses.text.primary}`}
                                >
                                  {medication.name}
                                </span>
                                {medication.dosage && (
                                  <span
                                    className={`${themeClasses.text.accent} ml-2 ${themeClasses.bg.tertiary} px-2 py-1 rounded text-xs`}
                                  >
                                    {medication.dosage}
                                  </span>
                                )}
                                {medication.instructions && (
                                  <div
                                    className={`${themeClasses.text.tertiary} text-xs mt-1 italic`}
                                  >
                                    📋 {medication.instructions}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span
                                className={`${themeClasses.bg.tertiary} rounded p-2 block`}
                              >
                                {medication}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                      {record.medications.length > 5 && (
                        <li
                          className={`${themeClasses.text.accent} text-xs mt-2`}
                        >
                          +{record.medications.length - 5} more medications...
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Line Items */}
              {record.lineItems && record.lineItems.length > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4
                      className={`text-sm font-semibold ${themeClasses.text.accent} flex items-center`}
                    >
                      <span className="mr-2">📋</span>
                      Services & Treatments ({record.lineItems.length}):
                    </h4>
                    <button
                      onClick={() => {
                        setEditingRecord(record.id);
                      }}
                      className={`text-xs ${themeClasses.text.accent} hover:${themeClasses.text.primary} px-2 py-1 border ${themeClasses.border.primary} rounded hover:${themeClasses.bg.secondary} transition-colors`}
                    >
                      View All
                    </button>
                  </div>

                  {editingRecord === record.id ? (
                    // Expanded view
                    <div
                      className={`${themeClasses.bg.secondary} rounded-lg p-3 border ${themeClasses.border.primary}`}
                    >
                      <div className="space-y-3 mb-3">
                        {(() => {
                          // Group items by category
                          const groupedItems = record.lineItems.reduce(
                            (groups, item) => {
                              const category = item.category || "General";
                              if (!groups[category]) {
                                groups[category] = [];
                              }
                              groups[category].push(item);
                              return groups;
                            },
                            {}
                          );

                          return Object.entries(groupedItems).map(
                            ([category, items]) => {
                              const firstItem = items[0];
                              const categoryTotal = items.reduce(
                                (sum, item) =>
                                  sum +
                                  (item.totalPrice ||
                                    item.unitPrice ||
                                    item.price ||
                                    item.cost ||
                                    item.amount ||
                                    item.total ||
                                    0),
                                0
                              );

                              return (
                                <div key={category} className="space-y-2">
                                  {/* Category Header */}
                                  <div
                                    className={`flex items-center justify-between p-2 ${themeClasses.bg.tertiary} rounded-lg border ${themeClasses.border.primary}`}
                                  >
                                    <div className="flex items-center space-x-2">
                                      {firstItem?.icon && (
                                        <span className="text-lg">
                                          {firstItem.icon}
                                        </span>
                                      )}
                                      <span
                                        className={`${themeClasses.text.primary} font-semibold text-sm`}
                                      >
                                        {category}
                                      </span>
                                      <span
                                        className={`${themeClasses.text.tertiary} text-xs`}
                                      >
                                        ({items.length} item
                                        {items.length !== 1 ? "s" : ""})
                                      </span>
                                    </div>
                                    <span
                                      className={`${themeClasses.text.success} font-bold text-sm`}
                                    >
                                      {formatCurrency(
                                        categoryTotal,
                                        record.currency
                                      )}
                                    </span>
                                  </div>

                                  {/* Category Items */}
                                  <div className="space-y-1 ml-4">
                                    {items.map((item, itemIdx) => (
                                      <div
                                        key={itemIdx}
                                        className={`flex justify-between items-center text-sm ${themeClasses.bg.secondary} rounded-lg p-2 border-l-4 border-gray-400`}
                                      >
                                        <div className="flex-1">
                                          <div className="flex items-center space-x-2">
                                            <span
                                              className={`${themeClasses.text.primary} font-medium`}
                                            >
                                              {item.description || "Service"}
                                            </span>
                                            {item.subcategory &&
                                              item.subcategory !== category && (
                                                <span
                                                  className={`px-2 py-1 text-xs rounded-full ${themeClasses.bg.tertiary} ${themeClasses.text.accent} font-medium`}
                                                >
                                                  {item.subcategory}
                                                </span>
                                              )}
                                          </div>
                                          {item.quantity && (
                                            <span
                                              className={`${themeClasses.text.tertiary} text-xs`}
                                            >
                                              (Qty: {item.quantity})
                                            </span>
                                          )}
                                        </div>
                                        <span
                                          className={`${themeClasses.text.success} font-bold`}
                                        >
                                          {formatCurrency(
                                            item.totalPrice ||
                                              item.unitPrice ||
                                              item.price ||
                                              item.cost ||
                                              item.amount ||
                                              item.total ||
                                              0,
                                            record.currency
                                          )}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            }
                          );
                        })()}
                      </div>
                      <div
                        className={`flex justify-between items-center pt-2 border-t ${themeClasses.border.primary}`}
                      >
                        <button
                          onClick={() => setEditingRecord(null)}
                          className={`text-xs ${themeClasses.text.tertiary} hover:${themeClasses.text.primary} px-3 py-1 border ${themeClasses.border.primary} rounded hover:${themeClasses.bg.secondary} transition-colors`}
                        >
                          Collapse
                        </button>
                        <div
                          className={`text-lg font-bold ${themeClasses.text.success}`}
                        >
                          Total:{" "}
                          {formatCurrency(
                            record.totalAmount ||
                              record.totalCost ||
                              record.lineItems.reduce(
                                (sum, item) =>
                                  sum +
                                  (item.totalPrice ||
                                    item.unitPrice ||
                                    item.price ||
                                    item.cost ||
                                    item.amount ||
                                    item.total ||
                                    0),
                                0
                              ),
                            record.currency
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Collapsed view
                    <div
                      className={`${themeClasses.bg.secondary} rounded-lg p-3 border ${themeClasses.border.primary}`}
                    >
                      <div className="space-y-1">
                        {record.lineItems.slice(0, 3).map((item, idx) => (
                          <div
                            key={idx}
                            className={`flex justify-between items-center text-sm ${themeClasses.bg.tertiary} rounded p-2`}
                          >
                            <span className={themeClasses.text.secondary}>
                              {item.description}
                              {item.quantity && ` (${item.quantity})`}
                            </span>
                            <span
                              className={`${themeClasses.text.success} font-medium`}
                            >
                              {formatCurrency(
                                item.totalPrice ||
                                  item.unitPrice ||
                                  item.price ||
                                  item.cost ||
                                  item.amount ||
                                  item.total ||
                                  0,
                                record.currency
                              )}
                            </span>
                          </div>
                        ))}
                        {record.lineItems.length > 3 && (
                          <div
                            className={`text-xs ${themeClasses.text.accent} text-center pt-2`}
                          >
                            +{record.lineItems.length - 3} more items • Click
                            "View All" to expand
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <InvoiceUpload
          user={user}
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            fetchHealthRecords();
          }}
        />
      )}

      {/* Record Detail Modal */}
      {selectedRecord && (
        <HealthRecordModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
};

export default HealthRecordsPage;
